// LandingPage.tsx
// Public marketing page shown to unauthenticated visitors.

import React, { useState, useEffect } from 'react';
import { SignIn } from '@clerk/clerk-react';
import ThemeToggle from './ThemeToggle';
import type { Theme } from '../types';
import styles from '../styles/LandingPage.module.css';

interface LandingPageProps {
  theme: Theme;
  onToggleTheme: () => void;
}

const DEMO_PAPERS = [
  { rank: 1, title: 'attention_is_all_you_need.pdf', type: 'Methods', verdict: 'must_read', verdictLabel: 'Must Read', overall: 4.8, novel: 5.0, rigor: 4.7, whatToRead: 'Full paper',    time: '~35 min' },
  { rank: 2, title: 'scaling_reinforcement_learni…', type: 'Mixed',   verdict: 'worth_it',  verdictLabel: 'Worth It',  overall: 3.6, novel: 3.6, rigor: 3.5, whatToRead: 'Methods only', time: '~22 min' },
  { rank: 3, title: 'Journal_of_Computer_Network…', type: 'Mixed',   verdict: 'skim',      verdictLabel: 'Skim It',   overall: 2.6, novel: 2.2, rigor: 2.6, whatToRead: 'Methods only', time: '~12 min' },
];

const TESTIMONIALS = [
  { emoji: '🧠', quote: "I used to spend 45 minutes deciding whether a paper was worth reading. Now I know in 30 seconds. My advisor thinks I've become a faster reader. I haven't told her.", name: 'PhD Candidate, Cognitive Science' },
  { emoji: '🤖', quote: "Desk Reject told me a paper I'd been meaning to read for months was 'skip it, the abstract lies.' It was right. I owe this app an apology and a beer.", name: 'Postdoc, ML Research' },
  { emoji: '📊', quote: "Finally, a tool that understands that 'novel contribution' and 'we reran existing baselines' are not the same thing.", name: 'MS Student, Data Science' },
];

const DIMENSIONS = [
  { name: 'Readability',        desc: 'Can you actually follow the argument?', score: 4.8 },
  { name: 'Technical Rigor',    desc: 'Are claims backed by evidence?',        score: 3.5 },
  { name: 'Novelty',            desc: 'Is this actually new?',                 score: 3.6 },
  { name: 'Writing Quality',    desc: 'Tight prose or AI-padded mush?',        score: 3.4 },
  { name: 'Vocabulary Profile', desc: 'Jargon earned or defensive?',           score: 3.7 },
  { name: 'Signal to Noise',    desc: 'Does the abstract match the body?',     score: 4.1 },
];

const TIERS = [
  { key: 'free',         emoji: '📄', name: 'Abstracts',    price: 'Free', priceNote: 'forever',          features: ['5 papers/month', 'Single upload', 'All 6 dimensions', 'Markdown export'],                 cta: 'Start free',   recommended: false },
  { key: 'reading_list', emoji: '📚', name: 'Reading List', price: '$6',   priceNote: '/mo · or $49/yr',  features: ['50 papers/month', 'Batch up to 5', 'All priority modes', 'CSV & Obsidian export'],       cta: 'Get started',  recommended: false },
  { key: 'dissertation', emoji: '🎓', name: 'Dissertation', price: '$15',  priceNote: '/mo · or $119/yr', features: ['200 papers/month', 'Batch up to 20', 'Zotero import', 'Ranked reading plan'],           cta: 'Most popular', recommended: true  },
  { key: 'lab',          emoji: '🔬', name: 'Lab',          price: '$39',  priceNote: '/mo · or $299/yr', features: ['Unlimited papers*', 'Unlimited batch', 'API access', 'Priority support'],               cta: 'Go unlimited', recommended: false },
];

const VERDICT_STYLE: Record<string, string> = {
  must_read: styles.verdictMustRead,
  worth_it:  styles.verdictWorthIt,
  skim:      styles.verdictSkim,
  skip:      styles.verdictSkip,
};

const SCORE_COLOR = (s: number) => s >= 4 ? styles.scoreHigh : s >= 3 ? styles.scoreMid : styles.scoreLow;
const BAR_COLOR   = (s: number) => s >= 4 ? styles.scoreBarHigh : s >= 3 ? styles.scoreBarMid : styles.scoreBarLow;

export default function LandingPage({ theme, onToggleTheme }: LandingPageProps) {
  const [showAuth, setShowAuth] = useState(false);
  const [barsReady, setBarsReady] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setBarsReady(true), 600);
    return () => clearTimeout(t);
  }, []);

  const openAuth = () => setShowAuth(true);

  return (
    <div className={styles.page}>

      {/* Nav */}
      <nav className={styles.nav}>
        <span className={styles.navLogo}>Desk Reject</span>
        <div className={styles.navActions}>
          <ThemeToggle theme={theme} onToggle={onToggleTheme} />
          <button className={styles.btnSignIn} onClick={openAuth}>Sign in</button>
          <button className={styles.btnNavCta} onClick={openAuth}>Try free →</button>
        </div>
      </nav>

      {/* Hero */}
      <section className={styles.hero}>
        <p className={styles.heroEyebrow}>AI paper triage for researchers</p>
        <h1 className={styles.heroHeadline}>
          Stop reading papers<br />that don’t deserve<br />your time.
        </h1>
        <p className={styles.heroSub}>
          Drop in a PDF. Get a verdict — Must Read, Worth It, Skim It, or Desk Reject —
          plus exactly which sections to read and why. In about 30 seconds.
        </p>
        <div className={styles.heroCtas}>
          <button className={styles.btnHeroPrimary} onClick={openAuth}>
            Start free — 5 papers/month →
          </button>
          <a href="#demo" className={styles.btnHeroSecondary}>
            See a sample result ↓
          </a>
        </div>
        <p className={styles.heroNote}>Free forever · No credit card · No crying</p>
      </section>

      {/* Problem strip */}
      <section className={styles.problemStrip}>
        <div className={styles.problemInner}>
          <p className={styles.problemQuote}>
            “The average academic paper takes 4–6 hours to read properly.
            Most of them aren’t worth 20 minutes.”
          </p>
          <p className={styles.problemAttrib}>— EVERY RESEARCHER, CONSTANTLY</p>
          <div className={styles.statsGrid}>
            {[
              { stat: '4–6 hrs', label: 'to read a paper properly' },
              { stat: '~30 sec',     label: 'to get a Desk Reject verdict' },
              { stat: '60%',         label: 'of papers skimmable or skippable' },
            ].map(({ stat, label }) => (
              <div key={stat}>
                <div className={styles.statNumber}>{stat}</div>
                <div className={styles.statLabel}>{label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How it works */}
      <section className={styles.howItWorks}>
        <h2 className={styles.sectionTitle}>How it works</h2>
        <div className={styles.stepsGrid}>
          {[
            { num: '01', icon: '📄', title: 'Drop in your PDF',      desc: "Single paper or a whole stack. Up to 20 at once on paid plans. We'll take it from there." },
            { num: '02', icon: '🎯', title: 'Tell us what you need', desc: 'Rigorous methods? New ideas? Just need an overview? We weight the scoring accordingly.' },
            { num: '03', icon: '⚡',     title: 'Get your verdict',      desc: 'Must Read, Worth It, Skim It, or Desk Reject — plus exactly which sections to read and why.' },
          ].map(step => (
            <div key={step.num} className={styles.stepCard}>
              <div className={styles.stepHeader}>
                <span className={styles.stepIcon}>{step.icon}</span>
                <span className={styles.stepNum}>STEP {step.num}</span>
              </div>
              <h3 className={styles.stepTitle}>{step.title}</h3>
              <p className={styles.stepDesc}>{step.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Demo */}
      <section id="demo" className={styles.demoSection}>
        <div className={styles.demoInner}>
          <div className={styles.demoHeader}>
            <h2 className={styles.sectionTitle}>A real reading plan. From real papers.</h2>
            <p className={styles.demoSub}>Three papers uploaded. Evaluated. Ranked. You saved 1 hr 12 min.</p>
          </div>
          <div className={styles.timeSavedBanner}>
            <span className={styles.timeSavedText}>
              Without triage: <strong>2 hr 21 min</strong> → After triage: <strong>1 hr 9 min</strong>
            </span>
            <span className={styles.timeSavedHighlight}>⏱ You saved 1 hr 12 min</span>
          </div>
          <div className={styles.resultsTable}>
            <div className={styles.tableHeader}>
              {['#','PAPER','TYPE','VERDICT','SCORE','NOVEL','RIGOR','WHAT TO READ','TIME'].map(h => (
                <span key={h} className={styles.tableHeaderCell}>{h}</span>
              ))}
            </div>
            {DEMO_PAPERS.map(paper => (
              <div key={paper.rank} className={styles.tableRow}>
                <span className={styles.tableRank}>{paper.rank}</span>
                <span className={styles.tableFilename}>{paper.title}</span>
                <span className={styles.typeBadge}>{paper.type}</span>
                <span className={`${styles.verdictBadge} ${VERDICT_STYLE[paper.verdict]}`}>{paper.verdictLabel}</span>
                <span className={`${styles.tableScore} ${SCORE_COLOR(paper.overall)}`}>{paper.overall}</span>
                <span className={styles.tableDimScore}>{paper.novel}</span>
                <span className={styles.tableDimScore}>{paper.rigor}</span>
                <span className={styles.tableWhatToRead}>{paper.whatToRead}</span>
                <span className={styles.tableTime}>{paper.time}</span>
              </div>
            ))}
          </div>
          <p className={styles.demoNote}>
            Each row is clickable in the real app — 6 scored dimensions, 30-second summary, quoted examples from the paper.
          </p>
        </div>
      </section>

      {/* Six dimensions */}
      <section className={styles.dimensionsSection}>
        <div className={styles.dimensionsHeader}>
          <h2 className={styles.sectionTitle}>Six dimensions. Weighted for you.</h2>
          <p className={styles.dimensionsSubtitle}>
            Not all papers are read for the same reason. Tell us what you’re optimizing for
            and the scoring shifts accordingly.
          </p>
        </div>
        <div className={styles.dimensionsGrid}>
          {DIMENSIONS.map(dim => (
            <div key={dim.name} className={styles.dimensionCard}>
              <div className={styles.dimensionInfo}>
                <div className={styles.dimensionName}>{dim.name}</div>
                <div className={styles.dimensionDesc}>{dim.desc}</div>
              </div>
              <div className={styles.dimensionScoreCol}>
                <div className={`${styles.dimensionScoreNum} ${SCORE_COLOR(dim.score)}`}>{dim.score}</div>
                <div className={styles.scoreBar}>
                  <div
                    className={`${styles.scoreBarFill} ${BAR_COLOR(dim.score)}`}
                    style={{ width: barsReady ? `${(dim.score / 5) * 100}%` : '0%' }}
                  />
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Zotero callout */}
      <section className={styles.zoteroSection}>
        <div className={styles.zoteroInner}>
          <div className={styles.zoteroIcon}>📦</div>
          <h2 className={styles.zoteroTitle}>
            Got a Zotero library?<br />We’ll rank the whole thing.
          </h2>
          <p className={styles.zoteroDesc}>
            Export your Zotero collection as a zip of PDFs. Drop it in.
            Get back a ranked reading plan sorted by must-read to skip.
            No other tool does this.
          </p>
          <span className={styles.zoteroBadge}>Available on Dissertation &amp; Lab plans</span>
        </div>
      </section>

      {/* Testimonials */}
      <section className={styles.testimonialsSection}>
        <h2 className={styles.sectionTitle}>People are saying things</h2>
        <div className={styles.testimonialsGrid}>
          {TESTIMONIALS.map(t => (
            <div key={t.name} className={styles.testimonialCard}>
              <div className={styles.testimonialEmoji}>{t.emoji}</div>
              <p className={styles.testimonialQuote}>“{t.quote}”</p>
              <div className={styles.testimonialName}>— {t.name}</div>
            </div>
          ))}
        </div>
      </section>

      {/* Pricing */}
      <section className={styles.pricingSection}>
        <div className={styles.pricingInner}>
          <div className={styles.pricingHeader}>
            <h2 className={styles.sectionTitle}>Pricing that doesn’t require a grant</h2>
            <p className={styles.pricingSubtitle}>Start free. Upgrade when your reading list gets out of hand.</p>
          </div>
          <div className={styles.pricingGrid}>
            {TIERS.map(tier => (
              <div key={tier.key} className={`${styles.tierCard} ${tier.recommended ? styles.tierCardRecommended : ''}`}>
                {tier.recommended && <div className={styles.tierRecommendedBadge}>★ Most popular</div>}
                <div className={styles.tierBody}>
                  <div className={styles.tierEmoji}>{tier.emoji}</div>
                  <div className={styles.tierName}>{tier.name}</div>
                  <div className={styles.tierPrice}>
                    {tier.price}<span className={styles.tierPriceNote}> {tier.priceNote}</span>
                  </div>
                  <hr className={styles.tierDivider} />
                  <ul className={styles.tierFeatures}>
                    {tier.features.map(f => (
                      <li key={f} className={styles.tierFeature}>
                        <span className={styles.tierFeatureCheck}>✓</span>{f}
                      </li>
                    ))}
                  </ul>
                  <button
                    className={tier.recommended ? styles.btnTierRecommended : styles.btnTier}
                    onClick={openAuth}
                  >
                    {tier.cta} →
                  </button>
                </div>
              </div>
            ))}
          </div>
          <p className={styles.pricingNote}>
            *Fair use policy applies above 500 papers/month · Annual plans save ~34% · 14-day trial on annual
          </p>
        </div>
      </section>

      {/* Final CTA */}
      <section className={styles.finalCta}>
        <h2 className={styles.finalCtaTitle}>Your reading list<br />won’t triage itself.</h2>
        <p className={styles.finalCtaDesc}>
          Five papers free, every month, no card required.
          That’s enough to know if this is for you.
        </p>
        <button className={styles.btnFinalCta} onClick={openAuth}>Start for free →</button>
      </section>

      {/* Footer */}
      <footer className={styles.footer}>
        <div>
          <span className={styles.footerBrand}>Desk Reject</span>
          <span className={styles.footerMade}>Made in Hawaiʻi 🌺</span>
        </div>
        <div className={styles.footerLinks}>
          <button className={styles.footerLink} onClick={openAuth}>Sign in</button>
          <a className={styles.footerLink} href="mailto:hi@desk-reject.com">Contact</a>
        </div>
      </footer>

      {/* Auth modal */}
      {showAuth && (
        <div
          className={styles.modalBackdrop}
          onClick={(e) => { if (e.target === e.currentTarget) setShowAuth(false); }}
        >
          <SignIn
            appearance={{
              elements: {
                card:               { background: 'var(--bg-card)', border: '1px solid var(--bg-card-border)', boxShadow: 'var(--shadow-lift)' },
                headerTitle:        { fontFamily: 'var(--font-display)', color: 'var(--text-primary)' },
                formButtonPrimary:  { background: 'var(--accent)', fontFamily: 'var(--font-mono)' },
              },
            }}
          />
        </div>
      )}

    </div>
  );
}
